import { useReducer, useRef, useState } from 'react'
import classNames from 'classnames'

import { getRegionalizedIngestionUrl } from 'common/api'
import ConfirmationModal from 'common/components/confirmationModal'
import Drawer from 'common/components/drawer'
import EmptyState from 'common/components/emptyState'
import PageHeader from 'common/components/PageHeader'
import PageTitle from 'common/components/PageTitle'
import { errorToast, infoToast } from 'common/components/toastNotification'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'

import { Product } from '../api/api'
import useUpdateCategoryDescriptionMutation from '../api/mutations/useUpdateCategoryDescriptionMutation'
import useUpdateProductDescriptionMutation from '../api/mutations/useUpdateProductDescriptionMutation'
import useGetCategoryDetailsQuery from '../api/queries/useGetCategoryDetailsQuery'
import useGetCategoryProductsQuery from '../api/queries/useGetCategoryProductsQuery'
import useGetProductCatalogCategoriesQuery from '../api/queries/useGetProductCatalogCategoriesQuery'
import useGetProductDetailsQuery from '../api/queries/useGetProductDetailsQuery'
import {
  fileNameConfirmed,
  fileProcessError,
  productUploadCompleted,
  productUploadProgress,
  productUploadStart,
} from '../store/dataSourceSlice'
import { Uploader } from '../upload'

import { CategoryProductsDrawer } from './CategoryProductsDrawer'
import CsvProductPreview from './CsvProductPreview'
import { csvProductInitialState, csvProductReducer } from './csvProductReducer'
import EditDescriptionModal from './editDescriptionModal'
import ProductCatalogTable from './ProductCatalogTable'
import SourceUploader from './SourceUploader'

type Props = {
  productCatalogs: {
    name: string
    id: string
    createdAt: string
    lastUpdated: string
    region: string
  }[]
}

type EditingItem = {
  type: 'category' | 'product'
  id: string
  name: string | undefined
  description: string | undefined
  path: string[]
  region: string
  catalogId: string
  categoryId?: string
}

const ProductCatalog = ({ productCatalogs }: Props) => {
  const csvUploaderProductRef = useRef<{ open: () => void }>(null)
  const fileRef = useRef<File | undefined>(undefined)
  const uploaderRef = useRef<Uploader | undefined>(undefined)
  const dispatch = useAppDispatch()
  const [csvProductState, csvProductDispatch] = useReducer(
    csvProductReducer,
    csvProductInitialState
  )
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const progressInPercentage = useAppSelector(
    (state) => state.dataSource.fileStates.product.progressPercentage
  )
  const isProcessing = useAppSelector(
    (state) => state.dataSource.fileStates.product.isProcessing
  )

  const [hasHeaderColumn, setHasHeaderColumn] = useState(true)
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false)

  const [selectedProductCatalogId, setSelectedProductCatalogId] = useState<
    string | undefined
  >(productCatalogs[0]?.id)
  const selectedProductCatalog = productCatalogs.find(
    (catalog) => catalog.id === selectedProductCatalogId
  )

  const productCatalogQuery = useGetProductCatalogCategoriesQuery({
    catalogId: selectedProductCatalogId!,
    region: selectedProductCatalog?.region,
  })

  const [selectedCnnPath, setSelectedCnnPath] = useState<string[]>([])
  const [selectedProductSearch, setSelectedProductSearch] =
    useState<{ productId: string; categoryId: string } | undefined>()

  const productDetailsQuery = useGetProductDetailsQuery({
    accountId,
    catalogId: selectedProductCatalogId,
    categoryId: selectedProductSearch?.categoryId,
    productId: selectedProductSearch?.productId,
    region: selectedProductCatalog?.region,
  })

  const categoryDetailsQuery = useGetCategoryDetailsQuery({
    catalogId: selectedProductCatalogId,
    ccnPath:
      selectedCnnPath.join('::') ||
      productDetailsQuery.data?.ccn_path.join('::'),
    region: selectedProductCatalog?.region,
  })
  const selectedProductCategoryId = categoryDetailsQuery.data?.id

  const categoryProductsQuery = useGetCategoryProductsQuery({
    catalogId: selectedProductCatalogId,
    categoryId: selectedProductCategoryId,
    region: selectedProductCatalog?.region,
  })

  const catalogName =
    productCatalogs.find((catalog) => catalog.id === selectedProductCatalogId)
      ?.name ?? ''

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null)

  const updateCategoryDescriptionMutation =
    useUpdateCategoryDescriptionMutation()
  const updateProductDescriptionMutation = useUpdateProductDescriptionMutation()

  const handleEditCategoryDescription = () => {
    if (!categoryDetailsQuery.data || !selectedProductCatalog) {
      return
    }

    setEditingItem({
      type: 'category',
      id: categoryDetailsQuery.data.id,
      name: categoryDetailsQuery.data.name,
      description: categoryDetailsQuery.data.description,
      path: categoryDetailsQuery.data.ccnPath,
      region: selectedProductCatalog.region,
      catalogId: selectedProductCatalog.id,
    })
    setIsEditModalOpen(true)
  }

  const handleEditProductDescription = (product: Product) => {
    if (!selectedProductCatalog || !selectedProductCategoryId) {
      return
    }

    setEditingItem({
      type: 'product',
      id: product.id,
      name: product.name,
      description: product.description,
      path: product.ccn_path,
      region: selectedProductCatalog.region,
      catalogId: selectedProductCatalog.id,
      categoryId: selectedProductCategoryId,
    })
    setIsEditModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsEditModalOpen(false)
    setEditingItem(null)
  }

  const handleSaveDescription = (newDescription: string) => {
    if (!editingItem || !accountId) {
      return
    }

    if (editingItem.type === 'category') {
      updateCategoryDescriptionMutation.mutate(
        {
          accountId,
          catalogId: editingItem.catalogId,
          categoryId: editingItem.id,
          region: editingItem.region,
          description: newDescription,
        },
        {
          onSuccess: handleCloseModal,
        }
      )
    } else if (editingItem.type === 'product' && editingItem.categoryId) {
      updateProductDescriptionMutation.mutate(
        {
          accountId,
          catalogId: editingItem.catalogId,
          categoryId: editingItem.categoryId,
          productId: editingItem.id,
          region: editingItem.region,
          description: newDescription,
        },
        {
          onSuccess: handleCloseModal,
        }
      )
    }
  }

  return (
    <div className="flex flex-col gap-12">
      <PageHeader>
        <div className="flex justify-between flex-1">
          <div className="pt-9">
            <PageTitle title="Catalogue" />
            <div className="text-coolGray-400">
              Configure the below settings to let Jacquard know what we should
              or should not use in the generation of your content.
            </div>
          </div>
        </div>
      </PageHeader>
      {productCatalogs.length > 0 &&
        selectedProductCatalogId &&
        isProcessing === false &&
        progressInPercentage === undefined && (
          <>
            <ProductCatalogTable
              catalogId={selectedProductCatalogId}
              productCatalogs={productCatalogs}
              onSearchChange={() => {
                setSelectedCnnPath([])
                setSelectedProductSearch(undefined)
              }}
              onCatalogChange={(id) => {
                setSelectedProductCatalogId(id)
                setSelectedCnnPath([])
                setSelectedProductSearch(undefined)
              }}
              onCategorySelect={(category) => {
                setSelectedCnnPath(category)
              }}
              onProductSelect={(productId, categoryId) => {
                setSelectedProductSearch({ productId, categoryId })
              }}
              onUploadClick={() => csvUploaderProductRef.current?.open()}
              productCatalog={productCatalogQuery.data}
              isLoading={productCatalogQuery.isFetching}
            />
            <Drawer
              isPush
              isOpen={selectedCnnPath.length > 0 || !!selectedProductSearch}
              onOpenChange={() => {
                setSelectedCnnPath([])
                setSelectedProductSearch(undefined)
              }}
              title={catalogName}
              content={
                <CategoryProductsDrawer
                  products={categoryProductsQuery.data}
                  isLoading={
                    selectedProductSearch === undefined
                      ? categoryProductsQuery.isLoading
                      : productDetailsQuery.isLoading ||
                        categoryDetailsQuery.isLoading
                  }
                  isFetchingNextPage={categoryProductsQuery.isFetchingNextPage}
                  categoryDetails={categoryDetailsQuery.data}
                  productDetails={productDetailsQuery.data}
                  fetchNextPage={categoryProductsQuery.fetchNextPage}
                  hasNextPage={categoryProductsQuery.hasNextPage}
                  onEditDescription={handleEditCategoryDescription}
                  onEditProductDescription={handleEditProductDescription}
                  isProductSelected={!!selectedProductSearch}
                />
              }
            />
          </>
        )}
      {progressInPercentage !== undefined && (
        <EmptyState
          title="Uploading your file"
          description={`Uploading is ${progressInPercentage}% complete`}
        />
      )}
      {isProcessing && (
        <EmptyState
          title="The file is being processed"
          description="This may take a few minutes. You will be notified when it is completed."
        />
      )}
      <div
        className={classNames({
          hidden:
            productCatalogs.length > 0 || progressInPercentage !== undefined,
        })}
      >
        <EmptyState
          title="Start creating your first record"
          description="We don't have any records stored."
          action={
            <SourceUploader
              ref={csvUploaderProductRef}
              fileName={undefined}
              progressInPercentage={undefined}
              hasHeaderColumn={hasHeaderColumn}
              onStartUpload={(file) => {
                fileRef.current = file
              }}
              onUpload={({ data, headers, fileName, numberOfRows }) => {
                csvProductDispatch({
                  type: 'upload',
                  data,
                  headers,
                  fileName,
                  numberOfRows,
                })
                dispatch(
                  productUploadStart({
                    fileName,
                    data,
                  })
                )
              }}
            />
          }
        />
      </div>
      {csvProductState.isPreviewOpen && fileRef.current && (
        <CsvProductPreview
          isOpen={csvProductState.isPreviewOpen}
          fileName={csvProductState.fileName}
          fileSize={fileRef.current.size}
          hasHeaderColumn={hasHeaderColumn}
          onChangeHeaderColumn={setHasHeaderColumn}
          numberOfRows={csvProductState.numberOfRows}
          data={csvProductState.data ?? []}
          onCancel={() => {
            setIsConfirmationVisible(true)
          }}
          onImport={(values) => {
            infoToast(
              'The upload has started. You will be notified when it is completed. Meanwhile, you can carry on with your work.'
            )

            csvProductDispatch({
              type: 'clickImport',
            })
            dispatch(
              fileNameConfirmed({
                fileName: values.productCatalogName,
              })
            )
            const file = fileRef.current

            if (file && values.privacyRegion) {
              const dataIngestionBaseUrl = getRegionalizedIngestionUrl(
                values.privacyRegion
              )
              uploaderRef.current = new Uploader({
                api: dataIngestionBaseUrl,
                fileName: values.productCatalogName,
                file: file,
                accountId,
              })
                .onProgress(({ percentage: newPercentage }) => {
                  if (newPercentage === 100) {
                    csvProductDispatch({ type: 'uploadCompleted' })
                    dispatch(productUploadCompleted())
                  } else if (newPercentage !== 100) {
                    csvProductDispatch({
                      type: 'uploadProgress',
                      percentage: newPercentage,
                    })
                    dispatch(
                      productUploadProgress({
                        percentage: newPercentage,
                      })
                    )
                  }
                })
                .onError(() => {
                  csvProductDispatch({ type: 'errorUpload' })
                  dispatch(fileProcessError())
                  errorToast(
                    'Something went wrong while uploading the file. Please try again or contact support.'
                  )
                })
              uploaderRef.current.start({
                data: {
                  useDataSources: true,
                  productCatalogueName: values.productCatalogName,
                  productCatalogueId: values.productCatalogId,

                  productIdColumnName: values.productIdColumn,
                  productNameColumnName: values.productNameColumn,
                  productDescriptionColumnName: values.descriptionColumn,

                  separatedBy: values.dataSeparator,
                  categoryDelimiter: values.delimiter,

                  ...(values.dataSeparator === 'delimiter' && {
                    categoryColumnName: values.categoryColumn,
                    categoryDescriptionColumnName:
                      values.categoryColumnDescription,
                  }),

                  ...(values.dataSeparator === 'columns' && {
                    ...removeEmptyDataFromArray(
                      values.categoryLevel,
                      values.categoryLevelDescription
                    ),
                  }),
                },
              })
            }
          }}
          productCatalogs={productCatalogs}
        />
      )}
      <ConfirmationModal
        open={isConfirmationVisible}
        title="Cancel preview"
        confirmationText="Are you sure you want to cancel the preview?"
        onCancel={() => setIsConfirmationVisible(false)}
        onConfirm={() => {
          csvProductDispatch({ type: 'cancelPreview' })
          setIsConfirmationVisible(false)
        }}
        confirmButtonText="Yes, cancel"
        cancelButtonText="No, keep previewing"
      />
      {editingItem && (
        <EditDescriptionModal
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveDescription}
          itemInfo={{
            id: editingItem.id,
            name: editingItem.name,
            path: editingItem.path,
            type: editingItem.type,
          }}
          initialDescription={editingItem?.description ?? ''}
          isLoading={
            updateCategoryDescriptionMutation.isLoading ||
            updateProductDescriptionMutation.isLoading
          }
        />
      )}
    </div>
  )
}

export default ProductCatalog

function removeEmptyDataFromArray(
  categoryLevel: string[],
  categoryLevelDescription: string[]
) {
  return {
    categoryLevelsColumnNames: categoryLevel.filter(
      (item, index) => item !== '' || categoryLevelDescription[index] !== ''
    ),
    categoryDescriptionLevelsColumnNames: categoryLevelDescription.filter(
      (item, index) => item !== '' || categoryLevel[index] !== ''
    ),
  }
}
